import gsap from 'gsap';
import appStore from '../utils/state';
import ScrollHeroItem from './scroll-hero-item';

export default class ScrollHero extends HTMLElement {
  private items: ScrollHeroItem[] = [];
  private labels: HTMLElement[] = [];
  private backgroundItems: ScrollHeroItem[] = [];
  private foregroundItems: ScrollHeroItem[] = [];
  private navButtons: HTMLButtonElement[] = [];
  private nav: HTMLElement | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onUpdateBound = this.onUpdate.bind(this);
  private onScrollBound = this.onScroll.bind(this);
  private onNavScrollBound = this.onNavScroll.bind(this);
  private onNavButtonClickBound = this.onNavButtonClick.bind(this);
  private onScrollStopBound = this.onScrollStop.bind(this);
  private onUserScrollInterruptBound = this.onUserScrollInterrupt.bind(this);
  private onMainNavOpenBound = this.onMainNavOpen.bind(this);
  private onSearchModalOpenBound = this.onSearchModalOpen.bind(this);

  private unsubscribeFromMainNavOpen: (() => void) | null = null;
  private unsubscribeFromSearchModalOpen: (() => void) | null = null;
  private rafId: number | null = null;
  private scrollStopTimeout: number | null = null;
  private isSnapping = false;
  private snapTween: gsap.core.Tween | null = null;
  private snapProxy = { y: 0 };
  private lastScrollY = 0;
  private scrollDirection: 'up' | 'down' | null = null;
  private currentItemIndex = 0;
  private navTargetIndex: number | null = null;
  private displayedLabelIndex: number | null = null;
  private isHeaderOverHero = false;

  private readonly scrollStopDelay = 100;
  private readonly snapEase = 'power3.out';
  private readonly snapThreshold = 3;
  private readonly snapDurationMin = 0.6;
  private readonly snapDurationMax = 1.2;

  private showLabelTl: gsap.core.Timeline | null = null;
  private hideLabelTl: gsap.core.Timeline | null = null;

  connectedCallback(): void {
    void this.init();
  }

  private async init(): Promise<void> {
    await customElements.whenDefined('scroll-hero-item');

    if (!this.isConnected) {
      return;
    }

    this.items = Array.from(this.querySelectorAll<ScrollHeroItem>('scroll-hero-item'));
    this.labels = Array.from(this.querySelectorAll('[data-scroll-hero-item-ref="labels"]')) as HTMLElement[];
    this.labels.forEach((label) => {
      label.style.pointerEvents = 'none';
    });

    this.navButtons = Array.from(this.querySelectorAll<HTMLButtonElement>('[data-scroll-hero-ref="nav-buttons"]'));
    this.nav = this.querySelector('[data-scroll-hero-ref="nav"]');
    this.backgroundItems = this.items.filter((item) => !item.isForeground);
    this.foregroundItems = this.items.filter((item) => item.isForeground);
    this.addEventListeners();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);
    window.addEventListener('scroll', this.onScrollBound, { passive: true });
    window.addEventListener('touchstart', this.onUserScrollInterruptBound, { passive: true });
    window.addEventListener('wheel', this.onUserScrollInterruptBound, { passive: true });

    this.navButtons.forEach((button) => {
      button.addEventListener('click', this.onNavButtonClickBound);
    });

    this.nav?.addEventListener('scroll', this.onNavScrollBound, { passive: true });

    this.unsubscribeFromMainNavOpen = appStore.subscribeKey('isMainNavOpen', this.onMainNavOpenBound);
    this.unsubscribeFromSearchModalOpen = appStore.subscribeKey('isSearchModalOpen', this.onSearchModalOpenBound);

    this.lastScrollY = window.scrollY;
    this.onResize();

    this.currentItemIndex = this.getCurrentItemIndex(window.scrollY);
    this.updateNavButtons(this.currentItemIndex);

    if (this.isWithinSection(window.scrollY)) {
      this.showLabelForIndex(this.currentItemIndex);
    }

    this.updateHeaderTheme();
    this.rafId = requestAnimationFrame(this.onUpdateBound);
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);
    window.removeEventListener('scroll', this.onScrollBound);
    window.removeEventListener('touchstart', this.onUserScrollInterruptBound);
    window.removeEventListener('wheel', this.onUserScrollInterruptBound);

    this.navButtons.forEach((button) => {
      button.removeEventListener('click', this.onNavButtonClickBound);
    });

    this.nav?.removeEventListener('scroll', this.onNavScrollBound);

    if (this.unsubscribeFromMainNavOpen) {
      this.unsubscribeFromMainNavOpen();
      this.unsubscribeFromMainNavOpen = null;
    }

    if (this.scrollStopTimeout !== null) {
      clearTimeout(this.scrollStopTimeout);
      this.scrollStopTimeout = null;
    }

    if (this.showLabelTl) {
      this.showLabelTl.kill();
      this.showLabelTl = null;
    }

    if (this.hideLabelTl) {
      this.hideLabelTl.kill();
      this.hideLabelTl = null;
    }

    if (this.snapTween) {
      this.snapTween.kill();
      this.snapTween = null;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  onResize(): void {
    this.resize();

    setTimeout(() => {
      this.resize();
    }, 100);
  }

  resize(): void {
    this.backgroundItems.forEach((item) => item.resize());

    this.foregroundItems.forEach((foreground) => {
      const background = this.backgroundItems.find((item) => item.index === foreground.index);

      if (background) {
        foreground.matchHeight(background.itemHeight);
      }

      foreground.resize();
    });

    this.updateNavScrollMasks();
    this.updateHeaderTheme();
  }

  private onNavScroll(): void {
    this.updateNavScrollMasks();
  }

  private onMainNavOpen(): void {
    this.updateHeaderTheme();
  }

  private onSearchModalOpen(): void {
    this.updateHeaderTheme();
  }

  onUpdate(): void {
    const scrollY = window.scrollY;
    let index: number;

    if (this.navTargetIndex !== null) {
      const targetItem = this.backgroundItems[this.navTargetIndex];
      const reachedTarget = targetItem && Math.abs(scrollY - targetItem.top) < this.snapThreshold;

      if (reachedTarget && !this.isSnapping) {
        this.navTargetIndex = null;
        index = this.getCurrentItemIndex(scrollY);
      } else {
        index = this.navTargetIndex;
      }
    } else {
      index = this.getCurrentItemIndex(scrollY);
    }

    if (index !== this.currentItemIndex) {
      this.currentItemIndex = index;
      this.updateNavButtons(index);
    }

    this.items.forEach((item) => item.update());
    this.updateHeaderTheme();
    this.rafId = requestAnimationFrame(this.onUpdateBound);
  }

  onScroll(): void {
    const scrollY = window.scrollY;

    if (this.displayedLabelIndex !== null && this.isWithinSection(scrollY)) {
      this.hideDisplayedLabel();
    }

    if (this.isSnapping) {
      this.lastScrollY = scrollY;
      return;
    }

    if (scrollY > this.lastScrollY) {
      this.scrollDirection = 'down';
    } else if (scrollY < this.lastScrollY) {
      this.scrollDirection = 'up';
    }

    this.lastScrollY = scrollY;

    if (this.scrollStopTimeout !== null) {
      clearTimeout(this.scrollStopTimeout);
    }

    this.scrollStopTimeout = window.setTimeout(this.onScrollStopBound, this.scrollStopDelay);
  }

  private onNavButtonClick(event: MouseEvent): void {
    const button = event.currentTarget as HTMLButtonElement;
    const index = parseInt(button.dataset.index ?? '0', 10);
    const item = this.backgroundItems[index];

    if (!item) {
      return;
    }

    if (index === this.currentItemIndex) {
      return;
    }

    this.navTargetIndex = index;
    this.currentItemIndex = index;
    this.updateNavButtons(index);
    this.hideDisplayedLabel();
    this.animateScrollTo(item.top);
  }

  private hideDisplayedLabel(): void {
    if (this.displayedLabelIndex === null) {
      return;
    }

    const label = this.labels[this.displayedLabelIndex];

    if (label) {
      this.hideLabel(label);
    }

    this.displayedLabelIndex = null;
  }

  private showLabelForIndex(index: number): void {
    const label = this.labels[index];

    if (!label || this.displayedLabelIndex === index) {
      return;
    }

    this.showLabel(label);
    this.displayedLabelIndex = index;
  }

  private showLabel(label: HTMLElement): void {
    if (this.hideLabelTl) {
      this.hideLabelTl.kill();
      this.hideLabelTl = null;
    }

    if (this.showLabelTl) {
      this.showLabelTl.kill();
      this.showLabelTl = null;
    }

    this.showLabelTl = gsap.timeline();
    this.showLabelTl.fromTo(label, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power2.out' }, 0);
    this.showLabelTl.fromTo(label, { y: 10 }, { y: 0, duration: 0.6, ease: 'expo.out' }, 0);
  }

  private hideLabel(label: HTMLElement): void {
    if (this.showLabelTl) {
      this.showLabelTl.kill();
      this.showLabelTl = null;
    }

    if (this.hideLabelTl) {
      this.hideLabelTl.kill();
      this.hideLabelTl = null;
    }

    this.hideLabelTl = gsap.timeline();
    this.hideLabelTl.to(label, { opacity: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideLabelTl.to(label, { y: 10, duration: 0.3, ease: 'power3.out' }, 0);
  }

  private onScrollStop(): void {
    this.scrollStopTimeout = null;

    const scrollY = window.scrollY;

    if (!this.isWithinSection(scrollY)) {
      this.hideDisplayedLabel();
      return;
    }

    if (this.shouldDisableSnap(scrollY)) {
      this.showLabelForIndex(this.currentItemIndex);
      return;
    }

    const vh = window.innerHeight;
    const visible = this.getVisibleBackgroundItems(scrollY, vh);

    if (visible.length < 2) {
      this.showLabelForIndex(this.currentItemIndex);
      return;
    }

    if (this.scrollDirection === null) {
      this.showLabelForIndex(this.currentItemIndex);
      return;
    }

    const snapY = this.getPeekSnapPointFromVisible(visible, this.scrollDirection, vh);

    this.animateScrollTo(snapY);
  }

  private animateScrollTo(targetY: number): void {
    const scrollY = window.scrollY;

    if (Math.abs(targetY - scrollY) < this.snapThreshold) {
      this.showLabelForIndex(this.currentItemIndex);
      return;
    }

    if (this.scrollStopTimeout !== null) {
      clearTimeout(this.scrollStopTimeout);
      this.scrollStopTimeout = null;
    }

    this.cancelSnap();

    this.isSnapping = true;
    this.snapProxy.y = scrollY;

    const distance = Math.abs(targetY - scrollY);
    const duration = this.getSnapDuration(distance);

    this.snapTween = gsap.to(this.snapProxy, {
      y: targetY,
      duration,
      ease: this.snapEase,
      onUpdate: () => {
        window.scrollTo(0, this.snapProxy.y);
      },
      onComplete: () => {
        this.isSnapping = false;
        this.snapTween = null;
        this.showLabelForIndex(this.currentItemIndex);
      },
    });
  }

  private cancelSnap(): void {
    if (this.snapTween) {
      this.snapTween.kill();
      this.snapTween = null;
    }

    this.isSnapping = false;
  }

  private onUserScrollInterrupt(): void {
    if (!this.isSnapping) {
      return;
    }

    this.navTargetIndex = null;
    this.cancelSnap();
  }

  private getSnapDuration(distance: number): number {
    const vh = window.innerHeight;
    const duration = (distance / vh) * 1.1;

    return Math.min(this.snapDurationMax, Math.max(this.snapDurationMin, duration));
  }

  private isItemInViewport(item: ScrollHeroItem, scrollY: number, vh: number): boolean {
    const itemBottom = item.top + item.itemHeight;

    return item.top < scrollY + vh && itemBottom > scrollY;
  }

  private getVisibleBackgroundItems(scrollY: number, vh: number): ScrollHeroItem[] {
    return this.backgroundItems.filter((item) => this.isItemInViewport(item, scrollY, vh));
  }

  private getPeekSnapPointFromVisible(visible: ScrollHeroItem[], direction: 'up' | 'down', vh: number): number {
    if (direction === 'down') {
      const peek = visible.reduce((furthest, item) => (item.top > furthest.top ? item : furthest));

      return peek.top;
    }

    const peek = visible.reduce((furthest, item) => (item.top < furthest.top ? item : furthest));

    return peek.top + peek.itemHeight - vh;
  }

  private shouldDisableSnap(scrollY: number): boolean {
    const lastItem = this.backgroundItems[this.backgroundItems.length - 1];

    if (!lastItem) {
      return false;
    }

    const vh = window.innerHeight;
    const tailStart = lastItem.top + lastItem.itemHeight - vh;

    return scrollY >= tailStart;
  }

  private getCurrentItemIndex(scrollY: number): number {
    const viewportMiddle = scrollY + window.innerHeight / 2;
    let index = 0;

    for (const item of this.backgroundItems) {
      if (item.top <= viewportMiddle) {
        index = item.index;
      } else {
        break;
      }
    }

    return index;
  }

  private updateNavButtons(index: number): void {
    this.navButtons.forEach((button) => {
      const buttonIndex = parseInt(button.dataset.index ?? '0', 10);
      const isActive = buttonIndex === index;

      button.classList.toggle('border-pure-white', isActive);
      button.classList.toggle('border-transparent', !isActive);
    });

    this.scrollNavToIndex(index);
    this.updateNavScrollMasks();
  }

  private scrollNavToIndex(index: number): void {
    if (!this.nav) {
      return;
    }

    const button = this.navButtons[index];
    const listItem = button?.parentElement;

    if (!listItem) {
      return;
    }

    gsap.to(this.nav, {
      scrollTop: listItem.offsetTop - 16,
      duration: 0.6,
      ease: 'power3.out',
    });
  }

  private updateNavScrollMasks(): void {
    if (!this.nav) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = this.nav;
    const threshold = 1;
    const canScrollUp = scrollTop > threshold;
    const canScrollDown = scrollTop + clientHeight < scrollHeight - threshold;

    this.nav.classList.toggle('scroll-hero-nav-mask-top', canScrollUp && !canScrollDown);
    this.nav.classList.toggle('scroll-hero-nav-mask-bottom', canScrollDown && !canScrollUp);
    this.nav.classList.toggle('scroll-hero-nav-mask-both', canScrollUp && canScrollDown);
  }

  private isHeroIntersectingViewport(): boolean {
    const rect = this.getBoundingClientRect();

    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  private updateHeaderTheme(): void {
    const intersects = this.isHeroIntersectingViewport();
    const isMainNavOpen = appStore.getState().isMainNavOpen;
    const isSearchModalOpen = appStore.getState().isSearchModalOpen;
    const shouldUseHeroHeader = intersects && !isMainNavOpen && !isSearchModalOpen;

    if (shouldUseHeroHeader === this.isHeaderOverHero) {
      return;
    }

    this.isHeaderOverHero = shouldUseHeroHeader;
    document.documentElement.toggleAttribute('data-scroll-hero-header', shouldUseHeroHeader);
  }

  private isWithinSection(scrollY: number): boolean {
    const firstItem = this.backgroundItems[0];
    const lastItem = this.backgroundItems[this.backgroundItems.length - 1];

    if (!firstItem || !lastItem) {
      return false;
    }

    const sectionStart = firstItem.top - firstItem.vh;
    const sectionEnd = lastItem.top + lastItem.itemHeight;

    return scrollY >= sectionStart && scrollY <= sectionEnd;
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    document.documentElement.removeAttribute('data-scroll-hero-header');
  }
}

customElements.define('scroll-hero', ScrollHero);
